import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Icon,
  Input,
  PageHeading,
  Search,
  Section,
  Stepper,
  Table,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import {
  ArrowLeft,
  ArrowRight,
  Bank,
  Buildings,
  CheckCircle,
  Circle,
  CircleNotch,
  Percent,
} from '@phosphor-icons/react';
import { PortalPayfacsLayout } from '../PortalPayfacsLayout';
import './CadastroNovoSeller.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlanoTaxa {
  id: number;
  nome: string;
  totalVendedores: number;
  dataCriacao: string; // ISO YYYY-MM-DD pra ordenação lexicográfica
}

interface PlanoDetalhe {
  pix:     { tempo: string; tipo: string; total: string; limite: string };
  boleto:  { tempo: string; tipo: string; markup: string; total: string; limite: string };
  debito:  { tempo: string; tipo: string; markup: string; taxaBarte: string; total: string };
  credito: { tempo: string; tipo: string; markup: string; taxaBarte: string; total: string };
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const PLANOS_INICIAIS: PlanoTaxa[] = [
  { id: 33, nome: 'Plano lojista 4821',                                                           totalVendedores: 3,  dataCriacao: '2025-08-12' },
  { id: 40, nome: 'Plano Zero Template',                                                          totalVendedores: 0,  dataCriacao: '2024-05-04' },
  { id: 52, nome: 'Plano - Euroluce comercio imp e exp de equip de ilum ltda',                    totalVendedores: 7,  dataCriacao: '2025-09-15' },
  { id: 57, nome: 'Plano - Allugator Serviços Digitais LTDA',                                     totalVendedores: 2,  dataCriacao: '2025-09-15' },
  { id: 60, nome: 'Plano - DESINCHA COMERCIO DE PRODUTOS NATURAIS LTDA',                          totalVendedores: 12, dataCriacao: '2025-09-15' },
  { id: 64, nome: 'Plano - Grupo Comercial Alpha LTDA',                                           totalVendedores: 6,  dataCriacao: '2025-09-20' },
  { id: 67, nome: 'Plano - Construção Forte LTDA',                                                totalVendedores: 14, dataCriacao: '2025-09-28' },
  { id: 70, nome: 'Plano Bronze Padrão',                                                          totalVendedores: 22, dataCriacao: '2024-12-01' },
];

function formatDateBR(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function getMockDetalhe(id: number): PlanoDetalhe {
  // Determinístico por id, valores realistas
  const variantes = [
    {
      pix:     { tempo: 'D + 1', tipo: 'Porcentagem', total: '0,80%', limite: 'R$ 10.000,00' },
      boleto:  { tempo: 'D + 2', tipo: 'Porcentagem', markup: '0,40%', total: '1,20%', limite: 'R$ 20.000,00' },
      debito:  { tempo: 'D + 1', tipo: 'Porcentagem', markup: '0,20%', taxaBarte: '0,10%', total: '1,00%' },
      credito: { tempo: 'D + 30', tipo: 'Porcentagem', markup: '0,60%', taxaBarte: '0,30%', total: '2,90%' },
    },
    {
      pix:     { tempo: 'D + 0', tipo: 'Porcentagem', total: '0,40%', limite: 'R$ 5.000,00' },
      boleto:  { tempo: 'D + 1', tipo: 'Porcentagem', markup: '0,00%', total: '0,80%', limite: 'R$ 12.000,00' },
      debito:  { tempo: 'D + 0', tipo: 'Porcentagem', markup: '0,00%', taxaBarte: '0,00%', total: '0,60%' },
      credito: { tempo: 'D + 1',  tipo: 'Porcentagem', markup: '0,20%', taxaBarte: '0,10%', total: '1,50%' },
    },
    {
      pix:     { tempo: 'D + 1', tipo: 'Porcentagem', total: '1,20%', limite: 'R$ 50.000,00' },
      boleto:  { tempo: 'D + 2', tipo: 'Porcentagem', markup: '0,80%', total: '1,50%', limite: 'R$ 50.000,00' },
      debito:  { tempo: 'D + 1', tipo: 'Porcentagem', markup: '0,60%', taxaBarte: '0,30%', total: '1,80%' },
      credito: { tempo: 'D + 30', tipo: 'Porcentagem', markup: '1,00%', taxaBarte: '0,30%', total: '3,40%' },
    },
  ];
  return variantes[id % variantes.length];
}

const STEPS = [
  { label: 'Dados da empresa' },
  { label: 'Endereço' },
  { label: 'Sócios' },
  { label: 'Dados bancários' },
  { label: 'Plano de taxas' },
];

interface PreCadastroCard {
  id: 'empresa' | 'bancario' | 'plano';
  icon: typeof Buildings;
  title: string;
  description: string;
  actionLabel?: string;
}

const PRECADASTRO_CARDS: PreCadastroCard[] = [
  {
    id: 'empresa',
    icon: Buildings,
    title: 'Dados da empresa',
    description: 'CNPJ, sócios, CNAE, endereço e contato',
  },
  {
    id: 'bancario',
    icon: Bank,
    title: 'Dados bancários',
    description: 'Conta para repasse das transações',
  },
  {
    id: 'plano',
    icon: Percent,
    title: 'Plano de taxas',
    description: 'Plano de taxas para vincular.',
    actionLabel: 'Criar plano »',
  },
];


// ─── Tabela de planos: colunas ─────────────────────────────────────────────

function buildPlanoColumns(
  selectedPlanId: number | null,
  hoveredId: number | null,
  setHoveredId: (id: number | null) => void,
  onVerDetalhes: (plano: PlanoTaxa) => void,
): ColumnDefinition<PlanoTaxa>[] {
  const hoverProps = (record: PlanoTaxa) => ({
    onMouseEnter: () => setHoveredId(record.id),
    onMouseLeave: () => setHoveredId(null),
  });

  return [
    {
      key: 'selection',
      title: '',
      dataIndex: 'id',
      width: 56,
      render: (_v, record) => {
        const isSelected = record.id === selectedPlanId;
        return (
          <div
            className={`plano-row-cell plano-row-radio ${isSelected ? 'plano-row-radio--selected' : ''}`}
            {...hoverProps(record)}
          >
            <Icon
              icon={isSelected ? CheckCircle : Circle}
              size={22}
              weight={isSelected ? 'fill' : 'regular'}
            />
          </div>
        );
      },
    },
    {
      key: 'nome',
      title: 'Nome do plano',
      dataIndex: 'nome',
      sortable: true,
      render: (v, record) => (
        <div className="plano-row-cell plano-row-cell--nome" {...hoverProps(record)}>
          <Text variant="body-sm" truncate>{v as string}</Text>
        </div>
      ),
    },
    {
      key: 'totalVendedores',
      title: 'Lojistas vinculados',
      dataIndex: 'totalVendedores',
      width: 180,
      align: 'center',
      sortable: true,
      render: (_v, record) => (
        <div className="plano-row-cell plano-row-cell--center" {...hoverProps(record)}>
          <Text variant="body-sm">{record.totalVendedores ?? '—'}</Text>
        </div>
      ),
    },
    {
      key: 'dataCriacao',
      title: 'Data de criação',
      dataIndex: 'dataCriacao',
      width: 160,
      sortable: true,
      render: (v, record) => (
        <div className="plano-row-cell" {...hoverProps(record)}>
          <Text variant="body-sm">{formatDateBR(v as string)}</Text>
        </div>
      ),
    },
    {
      key: 'detalhes',
      title: '',
      dataIndex: 'id',
      width: 160,
      align: 'right',
      render: (_v, record) => {
        const visible = hoveredId === record.id || selectedPlanId === record.id;
        return (
          <div
            className={`plano-row-cell plano-row-detalhes ${visible ? 'plano-row-detalhes--visible' : ''}`}
            {...hoverProps(record)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onVerDetalhes(record);
              }}
            >
              Ver detalhes
            </Button>
          </div>
        );
      },
    },
  ];
}

// ─── Sub-componente: Pré-cadastro (informativo) ────────────────────────────

interface PreCadastroProps {
  onIniciar: () => void;
  onCriarPlano: () => void;
}

function PreCadastro({ onIniciar, onCriarPlano }: PreCadastroProps) {
  return (
    <div className="precadastro-wrapper">
      <div className="precadastro-header">
        <Text variant="label-lg">
          Antes de começar, tenha tudo em mãos
        </Text>
        <Text variant="body-base" color="var(--text-secondary)">
          O lojista só poderá realizar transações após o preenchimento completo
          dos dados e a configuração das taxas e repasses.
        </Text>
      </div>

      <div className="precadastro-cards">
        {PRECADASTRO_CARDS.map((card) => (
          <div key={card.id} className="precadastro-card">
            <div className="precadastro-card__icon">
              <Icon icon={card.icon} size={22} weight="regular" />
            </div>
            <div className="precadastro-card__body">
              <Text variant="label-lg">{card.title}</Text>
              <Text variant="body-sm" color="var(--text-secondary)">
                {card.description}
              </Text>
            </div>
          </div>
        ))}
      </div>

      <div className="precadastro-actions">
        <Text variant="body-sm" color="var(--text-secondary)">
          Não tem plano pra esse lojista?{' '}
          <button
            type="button"
            className="precadastro-card__link"
            onClick={onCriarPlano}
          >
            Criar plano »
          </button>
        </Text>
        <Button
          onClick={onIniciar}
          size="lg"
          round="full"
          iconRight={<Icon icon={ArrowRight} size={20} weight="regular" />}
        >
          Iniciar cadastro
        </Button>
      </div>
    </div>
  );
}

// ─── Sub-componente: Step 1-4 (placeholders simples) ───────────────────────

interface StepFormProps {
  step: 1 | 2 | 3 | 4;
  onVoltar: () => void;
  onProximo: () => void;
}

function StepForm({ step, onVoltar, onProximo }: StepFormProps) {
  return (
    <div className="cadastro-step-body">
      <div className="cadastro-step-header">
        <div className="cadastro-step-header__text">
          <Text variant="label-lg">{STEPS[step - 1].label}</Text>
          <Text variant="body-sm" color="var(--text-secondary)">
            Preencha os dados desta etapa para continuar.
          </Text>
        </div>
      </div>

      {step === 1 && (
        <div className="cadastro-form-grid">
          <Input label="CNPJ / CPF" placeholder="Digite aqui o CNPJ ou CPF" className="cadastro-form-grid--full" />
          <Input label="Razão social" placeholder="Digite aqui o nome da empresa" />
          <Input label="Nome Fantasia" placeholder="Digite aqui o nome fantasia" />
          <Input label="CNAE" placeholder="Código CNAE" />
          <Input label="Data de abertura" placeholder="01/01/2001" />
          <Input label="E-mail" placeholder="Digite o e-mail do lojista" />
          <Input label="Telefone" placeholder="(11) 11111-1111" />
        </div>
      )}

      {step === 2 && (
        <div className="cadastro-form-grid">
          <Input label="CEP" placeholder="00000-000" />
          <Input label="Estado" placeholder="UF" />
          <Input label="Cidade" placeholder="Cidade" className="cadastro-form-grid--full" />
          <Input label="Endereço" placeholder="Rua, Avenida..." className="cadastro-form-grid--full" />
          <Input label="Número" placeholder="123" />
          <Input label="Complemento" placeholder="Sala, andar..." />
        </div>
      )}

      {step === 3 && (
        <div className="cadastro-form-grid">
          <Input label="Nome do sócio" placeholder="Digite o nome do sócio" />
          <Input label="CPF" placeholder="123.456.789-01" />
          <Input label="Data de nascimento" placeholder="01/01/1990" />
          <Input label="Participação (%)" placeholder="100" />
        </div>
      )}

      {step === 4 && (
        <div className="cadastro-form-grid">
          <Input label="Banco" placeholder="Código ou nome do banco" className="cadastro-form-grid--full" />
          <Input label="Agência" placeholder="0001" />
          <Input label="Conta" placeholder="00000-0" />
          <Input label="Tipo de conta" placeholder="Corrente / Poupança" />
          <Input label="Titular" placeholder="Nome do titular" />
        </div>
      )}

      <div className="cadastro-footer">
        <Button
          variant="ghost"
          onClick={onVoltar}
          iconLeft={<Icon icon={ArrowLeft} size={18} weight="regular" />}
        >
          Voltar
        </Button>
        <Button
          onClick={onProximo}
          iconRight={<Icon icon={ArrowRight} size={18} weight="regular" />}
        >
          Próximo
        </Button>
      </div>
    </div>
  );
}

// ─── Sub-componente: Step 5 (Plano de taxas) ───────────────────────────────

interface PlanoStepProps {
  planos: PlanoTaxa[];
  selectedPlanId: number | null;
  onSelectPlano: (id: number) => void;
  onVerDetalhes: (plano: PlanoTaxa) => void;
  onVoltar: () => void;
  onFinalizar: () => void;
  finalizando: boolean;
}

function PlanoStep({
  planos,
  selectedPlanId,
  onSelectPlano,
  onVerDetalhes,
  onVoltar,
  onFinalizar,
  finalizando,
}: PlanoStepProps) {
  const [page, setPage] = useState(1);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const pageSize = 8;

  const filteredPlanos = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return planos;
    return planos.filter((p) => p.nome.toLowerCase().includes(term));
  }, [planos, search]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPlanos.slice(start, start + pageSize);
  }, [filteredPlanos, page]);
  const totalPages = Math.max(1, Math.ceil(filteredPlanos.length / pageSize));

  const columns = buildPlanoColumns(selectedPlanId, hoveredId, setHoveredId, onVerDetalhes);

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  return (
    <div className="cadastro-step-body">
      <div className="cadastro-step-header">
        <div className="cadastro-step-header__text">
          <Text variant="label-lg">Plano de taxas</Text>
          <Text variant="body-sm" color="var(--text-secondary)">
            Selecione o plano que será aplicado às transações deste lojista.
          </Text>
        </div>
        <Search
          placeholder="Buscar plano..."
          expanded
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      <div className="content-table">
        <Table
          data={pagedData}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={(record) => onSelectPlano(record.id)}
          pagination={{
            currentPage: page,
            totalPages,
            onPageChange: setPage,
            pageSize,
            pageSizeOptions: [8],
            onPageSizeChange: () => {},
            totalItems: planos.length,
          }}
        />
      </div>

      <div className="cadastro-footer">
        <Button
          variant="ghost"
          onClick={onVoltar}
          iconLeft={<Icon icon={ArrowLeft} size={18} weight="regular" />}
        >
          Voltar
        </Button>
        <Button
          onClick={onFinalizar}
          disabled={selectedPlanId === null}
          loading={finalizando}
        >
          Finalizar cadastro
        </Button>
      </div>
    </div>
  );
}

// ─── Drawer: Criar novo plano ──────────────────────────────────────────────

interface DrawerCriarPlanoProps {
  isOpen: boolean;
  onClose: () => void;
  onSalvar: (nome: string) => void;
}

function DrawerCriarPlano({ isOpen, onClose, onSalvar }: DrawerCriarPlanoProps) {
  const [nome, setNome] = useState('');

  function handleSalvar() {
    if (!nome.trim()) return;
    onSalvar(nome.trim());
    setNome('');
  }

  function handleClose() {
    setNome('');
    onClose();
  }

  return (
    <Drawer
      isOpen={isOpen}
      className="cadastro-drawer-bottom"
      title="Cadastrar novo plano de taxas"
      onClose={handleClose}
      footer={{
        primaryButton: { children: 'Salvar', onClick: handleSalvar, disabled: !nome.trim() },
        secondaryButton: { children: 'Cancelar', onClick: handleClose },
      }}
    >
      <div className="cadastro-drawer-content">
        <Input
          label="Nome do plano"
          placeholder="Digite o nome do plano"
          value={nome}
          onChange={(e) => setNome((e.target as HTMLInputElement).value)}
        />

        <Section
          title="Pix"
          variant="two-columns"
          secondColumn={
            <>
              <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">Porcentagem</Text>} />
              <Section.Row label="Limite de taxas" content="-" />
            </>
          }
        >
          <Section.Row label="Tempo de repasse" content="-" />
          <Section.Row label="Total Cobrado" content="-" />
        </Section>

        <Section
          title="Boleto"
          variant="two-columns"
          secondColumn={
            <>
              <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">Porcentagem</Text>} />
              <Section.Row label="Total Cobrado" content="-" />
            </>
          }
        >
          <Section.Row label="Tempo de repasse" content="-" />
          <Section.Row label="Markup" content={<Text variant="label-sm">0,00%</Text>} />
          <Section.Row label="Limite de taxas" content="-" />
        </Section>

        <Section
          title="Cartão de Débito"
          variant="two-columns"
          secondColumn={
            <>
              <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">Porcentagem</Text>} />
              <Section.Row label="Taxa Barte" content={<Text variant="label-sm">0,00%</Text>} />
            </>
          }
        >
          <Section.Row label="Tempo de repasse" content="-" />
          <Section.Row label="Markup" content="-" />
          <Section.Row label="Total Cobrado" content={<Text variant="label-sm">0,00%</Text>} />
        </Section>

        <Section
          title="Cartão de Crédito"
          variant="two-columns"
          secondColumn={
            <>
              <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">Porcentagem</Text>} />
              <Section.Row label="Taxa Barte" content={<Text variant="label-sm">0,00%</Text>} />
            </>
          }
        >
          <Section.Row label="Tempo de repasse" content="-" />
          <Section.Row label="Markup" content="-" />
          <Section.Row label="Total Cobrado" content={<Text variant="label-sm">0,00%</Text>} />
        </Section>
      </div>
    </Drawer>
  );
}

// ─── Drawer: Ver detalhes do plano (modo leitura) ─────────────────────────

interface DrawerDetalhesProps {
  plano: PlanoTaxa | null;
  onClose: () => void;
}

function DrawerDetalhes({ plano, onClose }: DrawerDetalhesProps) {
  const detalhe = plano ? getMockDetalhe(plano.id) : null;

  return (
    <Drawer
      isOpen={!!plano}
      className="cadastro-drawer-bottom"
      title={plano ? `Detalhes — ${plano.nome}` : 'Detalhes'}
      onClose={onClose}
    >
      {plano && detalhe && (
        <div className="cadastro-drawer-content">
          <Section title="Identificação" variant="two-columns" secondColumn={null}>
            <Section.Row label="Nome do plano" content={<Text variant="label-sm">{plano.nome}</Text>} />
            <Section.Row label="Nº de lojistas vinculados" content={<Text variant="label-sm">{plano.totalVendedores}</Text>} />
          </Section>

          <Section
            title="Pix"
            variant="two-columns"
            secondColumn={
              <>
                <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">{detalhe.pix.tipo}</Text>} />
                <Section.Row label="Limite de taxas" content={<Text variant="label-sm">{detalhe.pix.limite}</Text>} />
              </>
            }
          >
            <Section.Row label="Tempo de repasse" content={<Text variant="label-sm">{detalhe.pix.tempo}</Text>} />
            <Section.Row label="Total Cobrado" content={<Text variant="label-sm">{detalhe.pix.total}</Text>} />
          </Section>

          <Section
            title="Boleto"
            variant="two-columns"
            secondColumn={
              <>
                <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">{detalhe.boleto.tipo}</Text>} />
                <Section.Row label="Total Cobrado" content={<Text variant="label-sm">{detalhe.boleto.total}</Text>} />
              </>
            }
          >
            <Section.Row label="Tempo de repasse" content={<Text variant="label-sm">{detalhe.boleto.tempo}</Text>} />
            <Section.Row label="Markup" content={<Text variant="label-sm">{detalhe.boleto.markup}</Text>} />
            <Section.Row label="Limite de taxas" content={<Text variant="label-sm">{detalhe.boleto.limite}</Text>} />
          </Section>

          <Section
            title="Cartão de Débito"
            variant="two-columns"
            secondColumn={
              <>
                <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">{detalhe.debito.tipo}</Text>} />
                <Section.Row label="Taxa Barte" content={<Text variant="label-sm">{detalhe.debito.taxaBarte}</Text>} />
              </>
            }
          >
            <Section.Row label="Tempo de repasse" content={<Text variant="label-sm">{detalhe.debito.tempo}</Text>} />
            <Section.Row label="Markup" content={<Text variant="label-sm">{detalhe.debito.markup}</Text>} />
            <Section.Row label="Total Cobrado" content={<Text variant="label-sm">{detalhe.debito.total}</Text>} />
          </Section>

          <Section
            title="Cartão de Crédito"
            variant="two-columns"
            secondColumn={
              <>
                <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">{detalhe.credito.tipo}</Text>} />
                <Section.Row label="Taxa Barte" content={<Text variant="label-sm">{detalhe.credito.taxaBarte}</Text>} />
              </>
            }
          >
            <Section.Row label="Tempo de repasse" content={<Text variant="label-sm">{detalhe.credito.tempo}</Text>} />
            <Section.Row label="Markup" content={<Text variant="label-sm">{detalhe.credito.markup}</Text>} />
            <Section.Row label="Total Cobrado" content={<Text variant="label-sm">{detalhe.credito.total}</Text>} />
          </Section>

          <BandeirasTaxas />
        </div>
      )}
    </Drawer>
  );
}

// ─── Bandeiras de cartão: tabela única com parcelas à esquerda ─────────────

const BANDEIRAS = ['Padrão', 'Mastercard', 'Hipercard', 'Elo', 'Amex'] as const;

const BANDEIRA_VALORES: Record<typeof BANDEIRAS[number], Array<{ markup: string; barte: string; total: string }>> = {
  'Padrão':    Array.from({ length: 6 }, () => ({ markup: '1,99', barte: '00,00', total: '1,99' })),
  'Mastercard':Array.from({ length: 6 }, () => ({ markup: '1,99', barte: '00,00', total: '1,99' })),
  'Hipercard': Array.from({ length: 6 }, () => ({ markup: '1,99', barte: '00,00', total: '1,99' })),
  'Elo':       Array.from({ length: 6 }, () => ({ markup: '1,99', barte: '00,00', total: '1,99' })),
  'Amex':      Array.from({ length: 6 }, () => ({ markup: '1,99', barte: '00,00', total: '1,99' })),
};

const BANDEIRA_TABS = ['Online', 'POS', 'Tap'] as const;
type BandeiraTab = typeof BANDEIRA_TABS[number];

function BandeirasTaxas() {
  const [tab, setTab] = useState<BandeiraTab>('Online');

  return (
    <div className="bandeiras-wrap">
      <div className="bandeiras-tabs">
        {BANDEIRA_TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`bandeiras-tab ${t === tab ? 'bandeiras-tab--active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="bandeiras-grid">
        {/* Linha 1: header de bandeiras */}
        <div className="bg-cell bg-cell--corner" />
        {BANDEIRAS.map((b) => (
          <div key={`h-${b}`} className="bg-cell bg-cell--bandeira-head">
            <Text variant="label-md">{b}</Text>
          </div>
        ))}

        {/* Linha 2: sub-headers (Markup / Barte / Total) */}
        <div className="bg-cell bg-cell--corner" />
        {BANDEIRAS.map((b) => (
          <div key={`sh-${b}`} className="bg-cell bg-cell--cols-head">
            <Text variant="caption-md" color="var(--text-secondary)">Markup</Text>
            <Text variant="caption-md" color="var(--text-secondary)">Barte</Text>
            <Text variant="caption-md" color="var(--text-secondary)">Total</Text>
          </div>
        ))}

        {/* Linhas de parcelas */}
        {Array.from({ length: 6 }, (_, i) => i + 1).map((p) => (
          <React.Fragment key={`row-${p}`}>
            <div className="bg-cell bg-cell--parcela">
              <Text variant="caption-md" color="var(--text-secondary)">{p}x</Text>
            </div>
            {BANDEIRAS.map((b) => {
              const v = BANDEIRA_VALORES[b][p - 1];
              return (
                <div key={`${b}-${p}`} className="bg-cell bg-cell--values">
                  <Text variant="body-sm">{v.markup}</Text>
                  <Text variant="body-sm" color="var(--text-secondary)">+</Text>
                  <Text variant="body-sm">{v.barte}</Text>
                  <Text variant="body-sm" color="var(--text-secondary)">=</Text>
                  <Text variant="body-sm">{v.total}</Text>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ─── Sub-componente: Sucesso ──────────────────────────────────────────────

interface SuccessStepProps {
  planoNome: string;
  onListar: () => void;
  onNovoCadastro: () => void;
}

function SuccessStep({ planoNome, onListar, onNovoCadastro }: SuccessStepProps) {
  const [phase, setPhase] = useState<'loading1' | 'loading2' | 'done'>('loading1');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('loading2'), 1100);
    const t2 = setTimeout(() => setPhase('done'), 2200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const isLoading = phase !== 'done';
  const titulo =
    phase === 'loading1'
      ? 'Cadastrando dados do lojista'
      : phase === 'loading2'
        ? 'Vinculando plano de taxas'
        : 'Lojista criado com sucesso';

  return (
    <div className={`success-shell ${phase === 'done' ? 'success-shell--complete' : ''}`}>
      <div className={`success-icon ${isLoading ? 'success-icon--loading' : 'success-icon--check'}`}>
        <Icon
          icon={isLoading ? CircleNotch : CheckCircle}
          size={64}
          weight={isLoading ? 'regular' : 'fill'}
        />
      </div>

      <div className="success-title" key={titulo}>
        <Text variant="label-lg">{titulo}</Text>
      </div>

      <div className="success-card">
        <Section title="" variant="two-columns" secondColumn={
          <>
            <Section.Row label="E-mail" content={<Text variant="label-sm">contato@comercio-brasil.com.br</Text>} />
            <Section.Row label="Plano de taxas" content={<Text variant="label-sm">{planoNome}</Text>} />
          </>
        }>
          <Section.Row label="Razão social" content={<Text variant="label-sm">Comércio Brasil LTDA</Text>} />
          <Section.Row label="CNPJ" content={<Text variant="label-sm">12.345.678/0001-90</Text>} />
        </Section>
      </div>

      <div className="success-actions">
        <Button variant="outline" onClick={onListar}>
          Ver todos os lojistas
        </Button>
        <Button onClick={onNovoCadastro}>
          Cadastrar novo
        </Button>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────

type CadastroStep = 0 | 1 | 2 | 3 | 4 | 5 | 'success';

export function CadastroNovoSeller() {
  // 0 = pré-cadastro (informativo); 1..5 = steps numerados; 'success' = pós-cadastro
  const [step, setStep] = useState<CadastroStep>(0);

  // Plano de taxas (step 5)
  const [planos, setPlanos] = useState<PlanoTaxa[]>(PLANOS_INICIAIS);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [drawerCriarOpen, setDrawerCriarOpen] = useState(false);
  const [drawerDetalhesPlano, setDrawerDetalhesPlano] = useState<PlanoTaxa | null>(null);

  const selectedPlano = planos.find((p) => p.id === selectedPlanId);

  function handleIniciar() {
    setStep(1);
  }

  function handleNext() {
    setStep((s) => (typeof s === 'number' && s < 5 ? ((s + 1) as CadastroStep) : s));
  }

  function handlePrev() {
    if (step === 1) {
      setStep(0);
    } else if (typeof step === 'number' && step > 1) {
      setStep((step - 1) as CadastroStep);
    }
  }

  function handleSalvarNovoPlano(nome: string) {
    const novoId = Math.max(...planos.map((p) => p.id), 100) + 1;
    const hoje = new Date().toISOString().slice(0, 10);
    const novoPlano: PlanoTaxa = { id: novoId, nome, totalVendedores: 0, dataCriacao: hoje };
    setPlanos((prev) => [novoPlano, ...prev]);
    setSelectedPlanId(novoId);
    setDrawerCriarOpen(false);
  }

  function handleFinalizar() {
    setStep('success');
  }

  function handleNovoCadastro() {
    setStep(0);
    setSelectedPlanId(null);
  }

  function handleListarLojistas() {
    window.location.href = '/portal-payfacs/transacoes';
  }

  return (
    <>
      <PortalPayfacsLayout activeItem="cadastro-novo-seller">
        <main className="content">
          <div className="content-inner">
            {step !== 'success' && <PageHeading title="Cadastro de novo lojista" />}

            {step !== 0 && step !== 'success' && (
              <div className="cadastro-stepper">
                <Stepper steps={STEPS} currentStep={step} />
              </div>
            )}

            {step === 0 && (
              <PreCadastro
                onIniciar={handleIniciar}
                onCriarPlano={() => setDrawerCriarOpen(true)}
              />
            )}

            {(step === 1 || step === 2 || step === 3 || step === 4) && (
              <StepForm step={step} onVoltar={handlePrev} onProximo={handleNext} />
            )}

            {step === 5 && (
              <PlanoStep
                planos={planos}
                selectedPlanId={selectedPlanId}
                onSelectPlano={setSelectedPlanId}
                onVerDetalhes={(p) => setDrawerDetalhesPlano(p)}
                onVoltar={handlePrev}
                onFinalizar={handleFinalizar}
                finalizando={false}
              />
            )}

            {step === 'success' && (
              <SuccessStep
                planoNome={selectedPlano?.nome ?? 'Plano vinculado'}
                onListar={handleListarLojistas}
                onNovoCadastro={handleNovoCadastro}
              />
            )}
          </div>
        </main>
      </PortalPayfacsLayout>

      <DrawerCriarPlano
        isOpen={drawerCriarOpen}
        onClose={() => setDrawerCriarOpen(false)}
        onSalvar={handleSalvarNovoPlano}
      />

      <DrawerDetalhes
        plano={drawerDetalhesPlano}
        onClose={() => setDrawerDetalhesPlano(null)}
      />
    </>
  );
}
